'use client';

import { useEffect, useState } from 'react';

import { AnimatedCounter } from 'react-animated-counter';

import { Palette } from 'DesignSystem2/Theme';

interface AnimatedProjectionTextProps {
    text: string;
    fontSize?: string;
    numberScrollAnimation: boolean;
    hideInitialAnimation?: boolean;
}

export const AnimatedProjectionText = ({
    text,
    fontSize,
    numberScrollAnimation,
    hideInitialAnimation = true,
}: AnimatedProjectionTextProps) => {
    // Hack to hide the initial animation on page load
    const [hideAnimation, setHideAnimation] = useState(hideInitialAnimation);

    // Cleared on unmount: without it the pending timer still fires and calls
    // `setHideAnimation` on a component that is gone. In the browser that is a
    // wasted render on a dead tree; under Vitest the test environment has already
    // been torn down by then, so React reads `window` while resolving the update
    // priority and the whole shard dies with "window is not defined" — an
    // unhandled error attributed to whichever unrelated test file happened to be
    // running one second later.
    useEffect(() => {
        const timer = setTimeout(() => setHideAnimation(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    if (!numberScrollAnimation) return <span>{text}</span>;

    return (
        <div className="flex items-center font-feature-number">
            {[...text].map((char, index) => {
                const parsedChar = parseInt(char);
                const key = `animated-counter-${index}-${char}`;

                if (isNaN(parsedChar))
                    return (
                        <div key={key}>
                            {/* A whitespace-only flex item collapses to zero width, so spaces
                                in the text (e.g. "172 companies") need a non-breaking space. */}
                            <span>{char === ' ' ? '\u00A0' : char}</span>
                        </div>
                    );

                return (
                    <div className="grid" key={key}>
                        {hideAnimation && <div style={{ gridColumn: 1, gridRow: 1 }}>{char}</div>}
                        <div style={{ opacity: hideAnimation ? 0 : 1, gridColumn: 1, gridRow: 1 }}>
                            <AnimatedCounter
                                key={`${index}-${parsedChar}`}
                                value={parsedChar}
                                includeDecimals={false}
                                incrementColor={Palette.Text.x10}
                                decrementColor={Palette.Text.x10}
                                fontSize={fontSize}
                                color="inherit"
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
    // Break down by unit into animated blocks
};
